import React, { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { IconButton, Stack, Tooltip, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

import CreateNewServiceContainer from 'components/v2/container/CreateNewServiceContainer';
import { useLocalization } from 'localization';
import { createNewOrderSchemaV2 } from 'components/v2/division/form/schema/createOrderSchema';
import { CreateNewOrderFormType, IAddress } from 'api';
import StepsProgress from 'components/v2/progress/StepsProgress';
import { ORDER_CREATION_PACKAGE_TYPE } from 'constants/ui/status';
import CreateOrderStep1 from './CreateOrderStep1';
import CreateOrderStep2 from './CreateOrderStep2';
import CreateOrderStep3 from './CreateOrderStep3';
import CreateOrderStep4 from './CreateOrderStep4';
import CreateOrderStep5 from './CreateOrderStep5';
import notifyHandler from 'utils/notifyHandler';
import routes from 'app.routes';
import { createNewOrderStore } from 'stores/createNewOrderStore';

export const DEFAULT_ORDER_CREATION_FORM: CreateNewOrderFormType = {
  address: {} as IAddress,
  property: { type: '', livingArea: 0, basementArea: 0, floors: 0 },
  hasExternalStylist: false,
  keyPickup: false,
  pickUpKeyAddress: {} as IAddress,
  shouldSendEndUserInvoice: false,
  endUserInvoice: {
    address: {} as IAddress,
  },
  shouldSendElectronicInvoice: false,
  electronicInvoice: {
    address: {} as IAddress,
  },
  branchId: '',
  case: { reference: '', isArchived: false },
  contactPersons: [
    {
      type: '',
      name: '',
      phone: '',
      phoneAlt: '',
    },
  ],
  explicitlySelectedDepartmentId: true,
  orderId: '',
  packageType: ORDER_CREATION_PACKAGE_TYPE.property,
  photographerComment: '',
  responsibleEmail: '',
  watcherEmails: [],
  status: 'draft',
  isCustomizePackage: false,
  products: [],
  productChoices: [],

  //new fields for v2
  basicPackage: undefined,
  webPackages: [],
  somePackages: [],
  textPackages: [],
  dronePackages: [],
  imageProducts: [],
  filmProducts: [],
  textProducts: [],
};

const CreateNewOrder = () => {
  const { t } = useLocalization();
  const { totalStep, discardFlow, goToStep, setFormData } = createNewOrderStore();
  const currentStep = createNewOrderStore(state => state.currentStep);
  const formData = createNewOrderStore(state => state.formData);
  const navigate = useNavigate();

  useEffect(() => {
    const hasCaseInfo = Boolean(formData?.case?.id || formData?.case?.reference);
    let timer: NodeJS.Timeout | null = null;

    if (!hasCaseInfo) {
      notifyHandler({ type: 'error', content: t('orders.pages.new.noCaseInfo') });

      timer = setTimeout(() => {
        navigate(routes.dashboard());
      }, 5000);
    }

    return () => {
      if (timer) clearTimeout(timer);
    };
  }, []);

  const methods = useForm({
    resolver: yupResolver(createNewOrderSchemaV2()),
    defaultValues: formData,
    mode: 'onChange',
    values: formData,
  });

  const stepsComponent: { [key: number]: React.JSX.Element } = {
    1: <CreateOrderStep1 />,
    2: <CreateOrderStep2 />,
    3: <CreateOrderStep3 />,
    4: <CreateOrderStep4 />,
    5: <CreateOrderStep5 />,
  };

  const stepsTitle: { [key: number]: string } = {
    1: `1. ${t('orders.pages.new.receipt.propertyInformationCardTitle')}`,
    2: `2. ${t('orders.pages.new.stepTwo.selectBasicPackage')}`,
    3: `3. ${t('orders.pages.new.stepThree.selectAddOns')}`,
    4: `4. ${t('orders.pages.new.stepFour.selectAdditionalProducts')}`,
    5: `5. ${t('orders.pages.new.receipt.title')}`,
  };

  const handleDiscardCreateOrder = () => {
    discardFlow();
    navigate(-1);
  };

  const handleGoToPreviousStep = () => {
    if (currentStep === 3) {
      //reset package selection when going back to step 2
      setFormData({
        ...formData,
        basicPackage: undefined,
      });
    }
    goToStep(currentStep - 1);
  };

  const stepTitleEl = () => {
    return (
      <Stack direction="row" alignItems="center" gap={0.5}>
        {(currentStep === 2 || currentStep === 3 || currentStep === 4) && (
          <Tooltip title={t('orders.pages.new.previousStep')} arrow>
            <IconButton aria-label="back" size="small" onClick={handleGoToPreviousStep}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
        )}

        <Typography
          variant="h5"
          sx={{
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {stepsTitle[currentStep]}
        </Typography>
      </Stack>
    );
  };

  return (
    <CreateNewServiceContainer
      title={stepTitleEl()}
      steps={<StepsProgress currentStep={currentStep} totalStep={totalStep} />}
      onClose={handleDiscardCreateOrder}
    >
      <FormProvider {...methods}>{stepsComponent[currentStep]}</FormProvider>
    </CreateNewServiceContainer>
  );
};

export default CreateNewOrder;
